import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment, isPaymentsConfigured } from "@/lib/stripe";
import { useAuth } from "@/lib/use-auth";
import { derivePremiumStatus } from "@/lib/premium-status";
import { tierFromChef, type PremiumTier } from "@/lib/chef-utils";

export interface SubscriptionInfo {
  plan: string;
  status: string;
  isPremium: boolean;
  /** Sprint 24.1 — Tier corrente derivato da chefs.premium_tier + premium_until. */
  tier: PremiumTier;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  source: string | null;
  loading: boolean;
}

export function useSubscription(): SubscriptionInfo {
  const { user } = useAuth();
  const [state, setState] = useState<SubscriptionInfo>({
    plan: 'free', status: 'active', isPremium: false, tier: 'free',
    currentPeriodEnd: null, cancelAtPeriodEnd: false, source: null, loading: true,
  });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!user) {
        if (!cancelled) setState((s) => ({ ...s, loading: false }));
        return;
      }
      const env = isPaymentsConfigured() ? getStripeEnvironment() : 'sandbox';

      // Fonte canonica per "isPremium": chefs.premium_until > now().
      // È env-agnostica e copre Founder/launch grant + Stripe.
      const [{ data: chef }, { data: subs }] = await Promise.all([
        supabase.from("chefs").select("premium_until, premium_tier").eq("user_id", user.id).maybeSingle(),
        // Includi le sub Stripe nel solo env corrente + tutte le sub 'launch' (env-agnostiche).
        supabase
          .from("subscriptions")
          .select("plan,status,current_period_end,cancel_at_period_end,source,environment,created_at")
          .eq("chef_id", user.id)
          .order("created_at", { ascending: false })
          .limit(10),
      ]);
      if (cancelled) return;

      const relevantSub =
        (subs ?? []).find((s) => s.source === "stripe" && s.environment === env) ||
        (subs ?? []).find((s) => s.source === "launch") ||
        (subs ?? [])[0] ||
        null;

      // SSoT (Sprint 3.1/3.2): la derivazione di isPremium passa esclusivamente
      // dal modulo premium-status. L'API pubblica del hook resta invariata.
      const { isPremium } = derivePremiumStatus({
        premium_until: chef?.premium_until ?? null,
      });
      const tier = tierFromChef({
        premium_tier: chef?.premium_tier ?? null,
        premium_until: chef?.premium_until ?? null,
      });

      setState({
        plan: relevantSub?.plan ?? (isPremium ? "premium_monthly" : "free"),
        status: relevantSub?.status ?? "active",
        isPremium,
        tier,
        currentPeriodEnd: relevantSub?.current_period_end ?? chef?.premium_until ?? null,
        cancelAtPeriodEnd: !!relevantSub?.cancel_at_period_end,
        source: relevantSub?.source ?? null,
        loading: false,
      });
    }
    load();
    return () => { cancelled = true; };
  }, [user]);

  return state;
}